import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import setupAxios from '../../../lib/redux/setupAxios'
import { clearMaintenance } from './_redux/maintenanceSlice'


const HEALTH_CHECK_PATH =
    process.env.REACT_APP_HEALTH_CHECK_PATH || '/health-check'
const POLL_INTERVAL_MS = 30_000

const MaintenanceWatcher = () => {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const { isMaintenance, recoveryToken } = useSelector(
        (state) => state.maintenance
    )
    const intervalRef = useRef(null)
    const inFlightRef = useRef(false)
    const lastHandledRecoveryTokenRef = useRef(recoveryToken)

    useEffect(() => {
        if (lastHandledRecoveryTokenRef.current === recoveryToken) return
        lastHandledRecoveryTokenRef.current = recoveryToken
        queryClient
            .invalidateQueries({ refetchType: 'active' })
            .catch(() => {})
    }, [recoveryToken, queryClient])

    useEffect(() => {
        if (!isMaintenance) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            inFlightRef.current = false
            return undefined
        }

        const runHealthCheck = async () => {
            if (inFlightRef.current) return
            inFlightRef.current = true
            try {
                await setupAxios.get(HEALTH_CHECK_PATH, {
                    skipMaintenanceTracking: true,
                    timeout: 10_000,
                    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
                    params: { _t: Date.now() },
                })
                dispatch(clearMaintenance())
            } catch (err) {
                const status = err?.response?.status
                if (status && status !== 503) {
                    dispatch(clearMaintenance())
                }
            } finally {
                inFlightRef.current = false
            }
        }

        runHealthCheck()
        intervalRef.current = setInterval(runHealthCheck, POLL_INTERVAL_MS)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [isMaintenance, dispatch])

    return null
}

export default MaintenanceWatcher
